import { useEffect, useState, useRef } from 'react'
import Layout from 'containers/Layout'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'
import apiGet from 'lib/network/apiGet'
import { forEach } from 'lodash'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import BlogProfile from './BlogProfile'
import LoadingIcon from 'components/atoms/LoadingIcon'
import classNames from 'classnames'

const AddEditBlog = () => {
  const history = useHistory()
  const formikRef = useRef(null)
  const submitActionRef = useRef('publish')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [published, setPublished] = useState(false)
  const [scheduleDateTime, setScheduleDateTime] = useState('')

  const [isEdit, setIsEdit] = useState(null)
  const [blogData, setBlogData] = useState({})
  const [loading, setLoading] = useState(false)

  const { id } = useParams()

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      fetchBlog(id)
    } else {
      setIsEdit(false)
    }
  }, [id])

  const fetchBlog = async () => {
    setLoading(true)
    try {
      const response = await apiGet(`blog/${id}`, {}, false)

      if (response?.success && response?.data) {
        const data = response.data

        // Clean up null string values
        forEach(Object.entries(data), ([key, value]) => {
          if (value === 'null') {
            delete data[key]
          }
        })

        setBlogData(data)
      } else {
        // If fetch fails, redirect to blogs list
        history.push('/blogs')
      }
    } catch (error) {
      // Error handled silently - could add error notification here if needed
      history.push('/blogs')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = action => {
    submitActionRef.current = action
    if (formikRef.current) {
      formikRef.current.handleSubmit()
    }
  }

  const canSchedule = scheduleDateTime && new Date(scheduleDateTime) > new Date()

  return (
    <Layout
      headerRight={
        <div className="mt-4 flex sm:mt-0 sm:ml-4 gap-3">
          <button
            onClick={() => {
              history.push('/blogs')
            }}
            type="button"
            className="order-0 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm text-normal rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit('schedule')}
            disabled={isSubmitting || !canSchedule}
            type="button"
            className={classNames(
              'order-0 inline-flex items-center px-6 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white transition-all duration-200',
              isSubmitting || !canSchedule
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            )}
          >
            {isSubmitting && submitActionRef.current === 'schedule' ? (
              <span className="flex items-center">
                <LoadingIcon className="mr-2 w-4 h-4" />
                Scheduling...
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Schedule
              </span>
            )}
          </button>
          <button
            onClick={() => handleSubmit('publish')}
            disabled={isSubmitting || !published}
            type="button"
            className={classNames(
              'order-0 inline-flex items-center px-6 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white transition-all duration-200',
              isSubmitting || !published
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
            )}
          >
            {isSubmitting && submitActionRef.current === 'publish' ? (
              <span className="flex items-center">
                <LoadingIcon className="mr-2 w-4 h-4" />
                {isEdit ? 'Updating...' : 'Publishing...'}
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Publish
              </span>
            )}
          </button>
        </div>
      }
      title={isEdit ? 'Edit Blog' : 'Add New Blog'}
      breadcrumbs={[
        { name: 'Blog List', pathname: '/blogs' },
        { name: isEdit ? 'Blog Details' : 'Add New Blog', pathname: isEdit ? `/blogs/edit/${id}/blog-profile` : '/add/blogs', current: true },
      ]}
    >
      <div className="flex overflow-auto">
        <Switch>
          <Route
            key="add"
            path="/add/blogs"
            render={() => (
              <BlogProfile
                id={null}
                isEdit={false}
                onSubmitRef={formikRef}
                submitActionRef={submitActionRef}
                onPublishChange={setPublished}
                onScheduleChange={setScheduleDateTime}
                onSubmittingChange={setIsSubmitting}
              />
            )}
          />
          <Route
            key="edit-profile"
            path="/blogs/edit/:id/blog-profile"
            render={() => (
              <BlogProfile
                readOnly={false}
                refetch={fetchBlog}
                blogData={blogData}
                setBlogData={setBlogData}
                loading={loading}
                id={id}
                isEdit={isEdit}
                onSubmitRef={formikRef}
                submitActionRef={submitActionRef}
                onPublishChange={setPublished}
                onScheduleChange={setScheduleDateTime}
                onSubmittingChange={setIsSubmitting}
              />
            )}
          />
          <Route
            key="edit"
            path="/blogs/edit/:id"
            render={() => (
              <BlogProfile
                readOnly={false}
                refetch={fetchBlog}
                blogData={blogData}
                setBlogData={setBlogData}
                loading={loading}
                id={id}
                isEdit={isEdit}
                onSubmitRef={formikRef}
                submitActionRef={submitActionRef}
                onPublishChange={setPublished}
                onScheduleChange={setScheduleDateTime}
                onSubmittingChange={setIsSubmitting}
              />
            )}
          />
        </Switch>
      </div>
    </Layout>
  )
}

export default AddEditBlog
